import { Component, useContext, useEffect, useState } from "react";
import {
  AppBar,
  Drawer,
  Grid2,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PeopleIcon from "@mui/icons-material/People"; // Patients
import BusinessIcon from "@mui/icons-material/Business"; // Facility
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Links, MainMenu } from "./Utility/Constants";
import AuthContext from "./Context/AuthProvider";
import { AccountCircle, FormatListBulletedOutlined, Menu as MenuIcon, ChevronLeft, ChevronRight, SwapHoriz } from "@mui/icons-material";
import {
  canAccess,
  canAccessEnquiry,
  getDisplayValue,
} from "./Utility/Utility";
import { clearAuth, saveAuth } from "./Utility/AuthManager";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import NoteAlt from "@mui/icons-material/NoteAlt";
import AnalyticsIcon from "@mui/icons-material/Analytics";

function MenuHeader1() {
  const [selectedMenuName, setSelectedMenuName] = useState(MainMenu.myWorklist);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const { auth } = useContext(AuthContext);
  const { setAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState();
  const [showFacilityDialog, setShowFacilityDialog] = useState(false);
  const [associatedFacilities, setAssociatedFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const location = useLocation();
  const path = location?.pathname;
  const mainMenuBorderWidth = 3;

  const drawerWidth = drawerExpanded ? 150 : 55;

  // Update main content margin using CSS
  const updateMainContentMargin = (expanded) => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.style.marginLeft = expanded ? '150px' : '55px';
    }
  };

  const toggleDrawer = () => {
    const newExpanded = !drawerExpanded;
    setDrawerExpanded(newExpanded);
    updateMainContentMargin(newExpanded);
  };

  // Get associated facilities from localStorage
  const getAssociatedFacilities = () => {
    try {
      const facilities = auth?.associatedFacilities;
      return facilities ? facilities : [];
    } catch (error) {
      console.error('Error retrieving associated facilities:', error);
      return [];
    }
  };

  // Handle facility change
  const handleChangeFacility = () => {
    const facilities = getAssociatedFacilities();
    setAssociatedFacilities(facilities);
    setSelectedFacilityId(auth?.selectedFacility?.id || '');
    setShowFacilityDialog(true);
  };

  // Handle facility selection
  const onFacilitySelection = () => {
    const selectedFacility = associatedFacilities.find(
      (a) => a.id === selectedFacilityId
    );
    
    if (selectedFacility) {
      localStorage.clear(); // Clear all localStorage data on facility change
      const authData = {
        associatedFacilities,
        userName: auth?.userName,
        authToken: auth?.authToken,
        selectedFacility,
        userFullName: auth?.userFullName,
        roles: auth?.roles,
      };
      setAuth(authData);
      saveAuth(authData);
      setShowFacilityDialog(false);
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setShowFacilityDialog(false);
    setSelectedFacilityId(auth?.selectedFacility?.id || '');
  };

  const CanAccessMyWorklist = canAccess({
    route: "MyWorklist",
    roles: auth?.roles,
  });

  const CanAccessVisits = canAccess({
    route: "Visits",
    roles: auth?.roles,
  });

  const CanAccessPlan = canAccess({
    route: "Plans",
    roles: auth?.roles,
  });

  const CanAccessBilling = canAccess({
    route: "Billing",
    roles: auth?.roles,
  });

  const CanAccessEnquiry = canAccess({
    route: "Enquiry",
    roles: auth?.roles,
  });

  const CanAccessReport = canAccess({
    route: "Report",
    roles: auth?.roles,
  });

  useEffect(() => {
    switch (path) {
      case Links.todayQueue:
      case Links.allAppointments:
      case Links.newAppointment:
        setSelectedMenuName(MainMenu.appointments);
        break;

      case Links.patientAnalytics:
      case Links.schedulingAnalytics:
        setSelectedMenuName(MainMenu.dashboard);
        break;

      case Links.root:
      case Links.myWorklist:
        setSelectedMenuName(MainMenu.myWorklist);
        break;

      case Links.facilityBasicDetails:
      case Links.facilityService:
      case Links.facilityStaff:
        setSelectedMenuName(MainMenu.facilitySettings);
        break;
      case Links.mySettings:
      case Links.profileSettings:
      case Links.availabilitySettings:
      case Links.prescriptionSettings:
        setSelectedMenuName(MainMenu.mySettings);
        break;

      case Links.billing:
        setSelectedMenuName(MainMenu.billing);
        break;

      case Links.patient:
        setSelectedMenuName(MainMenu.patients);
        break;
      case Links.enquiry:
        setSelectedMenuName(MainMenu.enquiry);
        break;
      case Links.plans:
        setSelectedMenuName(MainMenu.plans);
        break;
      case Links.reports:
        setSelectedMenuName(MainMenu.reports);
        break;

      default:
        break;
    }
  }, [path]);

  return (
    <div>
      <AppBar
        color="primary"
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          // width: `calc(100% - ${drawerWidth}px)`,
          // ml: `${drawerWidth}px`,
        }}
      >
        <Grid2 container size={12}>
          {/* Menu Toggle Button */}

          {/* Title Section */}
          <Grid2 container size={4} sx={{ alignContent: "center", alignItems: "center", paddingLeft: "10px" }}>
            
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
            >
              {drawerExpanded ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
            <Typography
              variant="h5"
              component="div"
              sx={{
                flexGrow: 1,
                fontSize: {
                  xs: "0.75rem", // Phones
                  sm: "0.875rem", // Tablets
                  md: "1rem", // Desktops
                  lg: "1.125rem", // Large Desktops
                  xl: "1.25rem", // Extra Large Desktops
                },
              }}
            >
              Click Health Pro
            </Typography>
          </Grid2>

          {/* Facility Name Section */}
          <Grid2
            container
            size={4}
            sx={{ alignItems: "center", justifyContent: "center" }}
          >
            {auth?.selectedFacility?.logo && (
              <Box
                component="img"
                src={auth?.selectedFacility?.logo}
                alt="logo"
                sx={{
                  height: {
                    xs: 15.5,
                    sm: 17.5,
                    md: 23.5,
                    lg: 25.5,
                    xl: 27.5,
                  },
                  width: {
                    xs: 15.5, // Phones (16 - 0.5)
                    sm: 17.5, // Tablets (18 - 0.5)
                    md: 23.5, // Desktops (22.5 + 1)
                    lg: 25.5, // Large Desktops (24.5 + 1)
                    xl: 27.5, // Extra Large Desktops (26.5 + 1)
                  },
                  marginRight: {
                    xs: 1, // 8px spacing for phones
                    sm: 1.5, // 12px spacing for tablets
                    md: 2, // 16px spacing for desktops and above
                  },
                  objectFit: "cover",
                  objectPosition: "center top",
                  borderRadius: "50%",
                }}
              />
            )}

            <Typography
              variant="h5"
              sx={{
                fontSize: {
                  xs: "0.75rem", // Phones
                  sm: "0.875rem", // Tablets
                  md: "1rem", // Desktops
                  lg: "1.125rem", // Large Desktops
                  xl: "1.25rem", // Extra Large Desktops
                },
                marginRight: 1,
              }}
            >
              {auth?.selectedFacility?.name}
            </Typography>

            <Tooltip title="Change Facility" arrow>
              <IconButton
                onClick={handleChangeFacility}
                color="inherit"
                size="small"
                sx={{
                  fontSize: {
                    xs: "16px",
                    sm: "18px",
                    md: "20px",
                  },
                  width: {
                    xs: "24px",
                    sm: "28px",
                    md: "32px",
                  },
                  height: {
                    xs: "24px",
                    sm: "28px", 
                    md: "32px",
                  },
                }}
              >
                <SwapHoriz fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Grid2>

          {/* User Welcome Section */}
          <Grid2 size={4}>
            {auth && (
              <Grid2
                container
                sx={{ alignItems: "center", justifyContent: "end" }}
              >
                <Grid2
                  container
                  sx={{ justifyContent: "end" }}
                  size={{ xs: 9, sm: 9, md: 10, lg: 11, xl: 11 }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontSize: {
                        xs: "0.75rem", // Phones
                        sm: "0.875rem", // Tablets
                        md: "1rem", // Desktops
                        lg: "1.125rem", // Large Desktops
                        xl: "1.25rem", // Extra Large Desktops
                      },
                    }}
                  >
                    Welcome, {auth?.userFullName}
                  </Typography>
                </Grid2>
                <Grid2
                  container
                  sx={{ justifyContent: "end" }}
                  size={{ xs: 3, sm: 3, md: 2, lg: 1, xl: 1 }}
                >
                  <IconButton
                    sx={{
                      fontSize: { xs: "18px", sm: "24px", md: "30px" }, // Adjust size based on screen
                      width: { xs: "32px", sm: "40px", md: "48px" },
                      height: { xs: "32px", sm: "40px", md: "48px" },
                    }}
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={() => setShowUserMenu(true)}
                    color="inherit"
                  >
                    <AccountCircle fontSize="inherit" />
                  </IconButton>
                </Grid2>
                <Menu
                  id="menu-appbar"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  open={showUserMenu}
                  onClose={() => setShowUserMenu(false)}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      clearAuth();
                      setAuth();
                      navigate("/");
                    }}
                  >
                    Log Out
                  </MenuItem>
                </Menu>
              </Grid2>
            )}
          </Grid2>
        </Grid2>
      </AppBar>

      <Drawer 
        variant="permanent" 
        anchor="left"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            transition: 'width 0.3s ease',
          },
        }}
      >
        <List
          sx={{
            paddingTop: {
              xs: "55px", // Smaller padding for phones
              sm: "60px", // Slightly larger padding for tablets
              md: "65px", // Default padding for desktops and larger screens
            },
            paddingX: "0px",
          }}
        >

          {/* My Worklist */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.myWorklist
                  ? mainMenuBorderWidth
                  : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessMyWorklist ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessMyWorklist ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessMyWorklist ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessMyWorklist) {
                    setSelectedMenuName(MainMenu.myWorklist);
                    navigate(Links.myWorklist);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessMyWorklist 
                      ? (selectedMenuName === MainMenu.myWorklist ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                  
                >
                  <FormatListBulletedOutlined />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.myWorklist)}
                  sx={{
                    color: CanAccessMyWorklist 
                      ? (selectedMenuName === MainMenu.myWorklist ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.myWorklist ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.myWorklist)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.myWorklist
                      ? "secondary"
                      : "default"
                  }
                  onClick={() => {
                    if (CanAccessMyWorklist) {
                      setSelectedMenuName(MainMenu.myWorklist);
                      navigate(Links.myWorklist);
                    }
                  }}
                  disabled={!CanAccessMyWorklist}
                >
                  <FormatListBulletedOutlined />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>

          {/* Appointments */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.appointments
                  ? mainMenuBorderWidth
                  : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessVisits ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessVisits ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessVisits ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessVisits) {
                    setSelectedMenuName(MainMenu.appointments);
                    navigate(Links.allAppointments);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessVisits 
                      ? (selectedMenuName === MainMenu.appointments ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                >
                  <CalendarMonthRoundedIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.visits)}
                  sx={{
                    color: CanAccessVisits 
                      ? (selectedMenuName === MainMenu.appointments ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.appointments ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.visits)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.appointments
                      ? "secondary"
                      : "default"
                  }
                  onClick={() => {
                    if (CanAccessVisits) {
                      setSelectedMenuName(MainMenu.appointments);
                      navigate(Links.allAppointments);
                    }
                  }}
                  disabled={!CanAccessVisits}
                >
                  <CalendarMonthRoundedIcon />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>

          {/* My Plan section */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.plans ? mainMenuBorderWidth : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessPlan ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessPlan ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessPlan ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessPlan) {
                    setSelectedMenuName(MainMenu.plans);
                    navigate(Links.plans);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessPlan 
                      ? (selectedMenuName === MainMenu.plans ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                >
                  <NoteAlt />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.plans)}
                  sx={{
                    color: CanAccessPlan 
                      ? (selectedMenuName === MainMenu.plans ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.plans ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.plans)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.plans ? "secondary" : "default"
                  }
                  onClick={() => {
                    if (CanAccessPlan) {
                      setSelectedMenuName(MainMenu.plans);
                      navigate(Links.plans);
                    }
                  }}
                  disabled={!CanAccessPlan}
                >
                  <NoteAlt />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>

          {/* Patients */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.patients
                  ? mainMenuBorderWidth
                  : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                component={Link}
                to={Links.patient}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  borderRadius: 1,
                  p: 1
                }}
                onClick={() => setSelectedMenuName(MainMenu.patients)}
              >
                <ListItemIcon
                  sx={{
                    color: selectedMenuName === MainMenu.patients ? "secondary.main" : "default",
                    minWidth: 40,
                  }}
                >
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.patients)}
                  sx={{
                    color: selectedMenuName === MainMenu.patients ? "secondary.main" : "default",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.patients ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.patients)}
                arrow
                placement="right"
              >
                <Link to={Links.patient}>
                  <IconButton
                    color={
                      selectedMenuName === MainMenu.patients
                        ? "secondary"
                        : "default"
                    }
                    onClick={() => setSelectedMenuName(MainMenu.patients)}
                  >
                    <PeopleIcon />
                  </IconButton>
                </Link>
              </Tooltip>
            )}
          </ListItem>

          {/* Billing */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.billing ? mainMenuBorderWidth : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessBilling ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessBilling ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessBilling ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessBilling) {
                    setSelectedMenuName(MainMenu.billing);
                    navigate(Links.billing);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessBilling 
                      ? (selectedMenuName === MainMenu.billing ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                >
                  <AccountBalanceWalletRoundedIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.billing)}
                  sx={{
                    color: CanAccessBilling 
                      ? (selectedMenuName === MainMenu.billing ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.billing ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.billing)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.billing
                      ? "secondary"
                      : "default"
                  }
                  onClick={() => {
                    if (CanAccessBilling) {
                      setSelectedMenuName(MainMenu.billing);
                      navigate(Links.billing);
                    }
                  }}
                  disabled={!CanAccessBilling}
                >
                  <AccountBalanceWalletRoundedIcon />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>

          {/* for enquiry section */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.enquiry ? mainMenuBorderWidth : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessEnquiry ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessEnquiry ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessEnquiry ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessEnquiry) {
                    setSelectedMenuName(MainMenu.enquiry);
                    navigate(Links.enquiry);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessEnquiry 
                      ? (selectedMenuName === MainMenu.enquiry ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                >
                  <ContactSupportIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.enquiry)}
                  sx={{
                    color: CanAccessEnquiry 
                      ? (selectedMenuName === MainMenu.enquiry ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.enquiry ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.enquiry)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.enquiry
                      ? "secondary"
                      : "default"
                  }
                  onClick={() => {
                    if (CanAccessEnquiry) {
                      setSelectedMenuName(MainMenu.enquiry);
                      navigate(Links.enquiry);
                    }
                  }}
                  disabled={!CanAccessEnquiry}
                >
                  <ContactSupportIcon />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>
          {/* for report section  */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.reports ? mainMenuBorderWidth : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: CanAccessReport ? 'pointer' : 'not-allowed',
                  '&:hover': CanAccessReport ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                  borderRadius: 1,
                  p: 1,
                  opacity: CanAccessReport ? 1 : 0.5,
                }}
                onClick={() => {
                  if (CanAccessReport) {
                    setSelectedMenuName(MainMenu.reports);
                    navigate(Links.reports);
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: CanAccessReport 
                      ? (selectedMenuName === MainMenu.reports ? "secondary.main" : "default")
                      : "text.disabled",
                    minWidth: 40,
                  }}
                >
                  <AnalyticsIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.reports)}
                  sx={{
                    color: CanAccessReport 
                      ? (selectedMenuName === MainMenu.reports ? "secondary.main" : "default")
                      : "text.disabled",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.reports ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.reports)}
                arrow
                placement="right"
              >
                <IconButton
                  color={
                    selectedMenuName === MainMenu.reports
                      ? "secondary"
                      : "default"
                  }
                  onClick={() => {
                    if (CanAccessReport) {
                      setSelectedMenuName(MainMenu.reports);
                      navigate(Links.reports);
                    }
                  }}
                  disabled={!CanAccessReport}
                >
                  <AnalyticsIcon />
                </IconButton>
              </Tooltip>
            )}
          </ListItem>

          {/* Facility */}
          <ListItem
            sx={{
              paddingX: "5px",
              paddingY: {
                xs: "0.25rem", // Smaller padding for phones
                sm: "0.5rem", // Slightly larger padding for tablets
                md: "1px", // Default padding for desktops and larger screens
              },
              borderColor: "secondary.main",
              borderStyle: "solid",
              borderRightWidth:
                selectedMenuName === MainMenu.facilitySettings
                  ? mainMenuBorderWidth
                  : 0,
              borderLeftWidth: 0,
              borderBottomWidth: 0,
              borderTopWidth: 0,
            }}
          >
            {drawerExpanded ? (
              <Box
                component={Link}
                to={Links.facilityBasicDetails}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  width: '100%',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  borderRadius: 1,
                  p: 1
                }}
                onClick={() => setSelectedMenuName(MainMenu.facilitySettings)}
              >
                <ListItemIcon
                  sx={{
                    color: selectedMenuName === MainMenu.facilitySettings ? "secondary.main" : "default",
                    minWidth: 40,
                  }}
                >
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={getDisplayValue(MainMenu.facilitySettings)}
                  sx={{
                    color: selectedMenuName === MainMenu.facilitySettings ? "secondary.main" : "default",
                    '& .MuiListItemText-primary': {
                      fontWeight: selectedMenuName === MainMenu.facilitySettings ? 'bold' : 'normal',
                    }
                  }}
                />
              </Box>
            ) : (
              <Tooltip
                title={getDisplayValue(MainMenu.facilitySettings)}
                arrow
                placement="right"
              >
                <Link to={Links.facilityBasicDetails}>
                  <IconButton
                    color={
                      selectedMenuName === MainMenu.facilitySettings
                        ? "secondary"
                        : "default"
                    }
                    onClick={() => setSelectedMenuName(MainMenu.facilitySettings)}
                  >
                    <BusinessIcon />
                  </IconButton>
                </Link>
              </Tooltip>
            )}
          </ListItem>
        </List>
      </Drawer>

      {/* Facility Selection Dialog */}
      <Dialog
        open={showFacilityDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Switch Facility</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ marginBottom: 2 }}>
            Please save any unsaved work before switching facilities to avoid losing changes.
          </Alert>
          <FormControl fullWidth sx={{ marginTop: 1 }}>
            <InputLabel id="facility-select-label">Select Facility</InputLabel>
            <Select
              labelId="facility-select-label"
              value={selectedFacilityId}
              label="Select Facility"
              onChange={(e) => setSelectedFacilityId(e.target.value)}
            >
              {associatedFacilities.map((facility) => (
                <MenuItem key={facility.id} value={facility.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        height: 20,
                        width: 20,
                        marginRight: 1,
                        borderRadius: "50%",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: facility.logo ? 'transparent' : '#f0f0f0',
                        border: facility.logo ? 'none' : '1px solid #ddd',
                      }}
                    >
                      {facility.logo ? (
                        <Box
                          component="img"
                          src={facility.logo}
                          alt="logo"
                          sx={{
                            height: 20,
                            width: 20,
                            objectFit: "cover",
                            objectPosition: "center top",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <BusinessIcon sx={{ fontSize: 12, color: '#999' }} />
                      )}
                    </Box>
                    <Typography>{facility.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={onFacilitySelection}
            variant="contained"
            disabled={!selectedFacilityId || selectedFacilityId === auth?.selectedFacility?.id}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MenuHeader1;
